// src/pages/doctor/DoctorQuestionnaire.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

interface VisibleCondition {
  id: string;
  equals?: string[];
  notEquals?: string[];
  includes?: string[];
  notIncludes?: string[];
}

interface QuestionItem {
  id: string;
  text: { ja: string; zh: string };
  type?: 'choice' | 'multi_choice' | 'short_text' | 'long_text' | 'number';
  options?: { ja: string[]; zh: string[] };
  required?: boolean;
  visible_if?: VisibleCondition[];
}

type QuestionsRaw =
  | QuestionItem[]
  | { first?: QuestionItem[]; followup?: QuestionItem[] };

type LangKey = 'ja' | 'zh';
type UsedGroup = 'first' | 'followup' | 'unknown';

/** 静的ファイルのURL（Vite の BASE_URL を考慮） */
const dataUrl = (filename: string) => {
  // eslint-disable-next-line
  const base = process.env.PUBLIC_URL || '/';
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}data/${filename}`;
};

// === 直前の検索クエリをページ毎に記憶（DoctorLayout/DoctorRecord と同様の流儀） ===
const SS_KEY = 'doctor.lastSearchByPath';
const getSavedSearchForPath = (path: string): string => {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return '';
    const map = JSON.parse(raw) as Record<string, string>;
    return map[path] || '';
  } catch {
    return '';
  }
};

/** 質問定義の読み込み（候補ファイル名を順に試行） */
async function loadQuestionsWithFallback(
  serviceName: string
): Promise<{ raw: QuestionsRaw | null; used?: string }> {
  const name = serviceName.trim();
  const candidates = Array.from(
    new Set(
      [
        `${name}_questions.json`,
        `${name.replace(/-/g, '_')}_questions.json`,
        `${name.replace(/_/g, '')}_questions.json`,
        name.includes('aga')
          ? `aga_${name.includes('female') ? 'female' : 'male'}_questions.json`
          : '',
        name.includes('obesity')
          ? `obesity_${name.includes('female') ? 'female' : 'male'}_questions.json`
          : '',
        name === 'ed' || name === 'ed_premature' ? 'ed_premature_questions.json' : '',
        name === 'pill' ? 'pill_questions.json' : '',
      ].filter(Boolean)
    )
  );

  for (const filename of candidates) {
    try {
      const res = await fetch(dataUrl(filename), { cache: 'no-store' });
      if (!res.ok) continue;
      const txt = await res.text();
      const json = JSON.parse(txt) as QuestionsRaw;
      return { raw: json, used: filename };
    } catch {
      // 次の候補へ
    }
  }
  return { raw: null };
}

/** 文字の正規化：() → （）、全角/半角空白除去 */
const normalizeToken = (s: string): string =>
  String(s || '')
    .replace(/\(/g, '（')
    .replace(/\)/g, '）')
    .replace(/\s+/g, '')
    .trim();

/** 値を配列へ正規化（配列/「、」区切り文字列/空値を意識） + トークン正規化 */
const toArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return (v as string[]).map(normalizeToken).filter(Boolean);
  if (typeof v === 'string' && v.trim().length > 0) {
    return v
      .split('、')
      .map((s) => normalizeToken(s))
      .filter(Boolean);
  }
  return [];
};

/** 回答が実質入力ありか（空文字・空配列・null/undefined は未回答） */
const isAnswered = (v: unknown): boolean => {
  if (v === null || v === undefined) return false;
  if (Array.isArray(v)) return (v as unknown[]).length > 0;
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
};

/** ★可視条件の評価：依存IDの回答が未入力の場合は「不可視」にする */
const isVisible = (q: QuestionItem, answers: Record<string, any>): boolean => {
  const conds = q.visible_if;
  if (!conds || conds.length === 0) return true;

  return conds.every((cond) => {
    const raw = answers[cond.id];

    // 依存回答が無い or 空 → 不可視
    if (!isAnswered(raw)) return false;

    const arr = toArray(raw);
    const scalar = Array.isArray(raw) ? '' : normalizeToken(String(raw ?? ''));

    const eq = (cond.equals || []).map(normalizeToken);
    const neq = (cond.notEquals || []).map(normalizeToken);
    const inc = (cond.includes || []).map(normalizeToken);
    const ninc = (cond.notIncludes || []).map(normalizeToken);

    if (eq.length > 0) {
      const ok = Array.isArray(raw)
        ? eq.some((v) => arr.includes(v))
        : eq.includes(scalar);
      if (!ok) return false;
    }

    if (neq.length > 0) {
      const ng = Array.isArray(raw)
        ? neq.some((v) => arr.includes(v))
        : neq.includes(scalar);
      if (ng) return false;
    }

    if (inc.length > 0) {
      if (!inc.some((v) => arr.includes(v))) return false;
    }

    if (ninc.length > 0) {
      if (ninc.some((v) => arr.includes(v))) return false;
    }

    return true;
  });
};

/** raw から題組を選択（first/followup のヒット数で推定） */
function pickQuestions(
  raw: QuestionsRaw | null,
  answers: Record<string, any>
): { list: QuestionItem[]; group: UsedGroup } {
  if (!raw) return { list: [], group: 'unknown' };

  // 旧形式：そのまま
  if (Array.isArray(raw)) return { list: raw, group: 'unknown' };

  const first = raw.first || [];
  const follow = raw.followup || [];

  if (!first.length && !follow.length) return { list: [], group: 'unknown' };
  if (first.length && !follow.length) return { list: first, group: 'first' };
  if (!first.length && follow.length) return { list: follow, group: 'followup' };

  const countHit = (arr: QuestionItem[]) =>
    arr.reduce((n, q) => (isAnswered(answers[q.id]) ? n + 1 : n), 0);

  const cFirst = countHit(first);
  const cFollow = countHit(follow);

  if (cFirst === 0 && cFollow === 0) {
    return { list: first, group: 'first' };
  }
  if (cFollow > cFirst) return { list: follow, group: 'followup' };
  return { list: first, group: 'first' };
}

/** 数値パース用ヘルパー（"170cm" → 170 などを想定） */
const parseNumber = (v: any): number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const cleaned = v.replace(/[^0-9.]/g, '');
    if (!cleaned) return null;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** 肥満メニュー用 BMI 計算 */
const calcBmiFromAnswers = (
  serviceName: string,
  answers: Record<string, any>
): number | null => {
  // 男性/女性メディカルダイエットのみ
  if (!serviceName.startsWith('obesity')) return null;

  const keys = Object.keys(answers || {});

  // 身長・体重に対応しそうなキーをざっくり拾う
  const heightKey =
    keys.find(
      (k) =>
        k.toLowerCase().includes('height') || // height, height_cm など
        k.includes('身長')
    ) || null;

  const weightKey =
    keys.find(
      (k) =>
        k.toLowerCase().includes('weight') || // weight, weight_kg など
        k.includes('体重')
    ) || null;

  if (!heightKey || !weightKey) return null;

  const hVal = parseNumber(answers[heightKey]);
  const wVal = parseNumber(answers[weightKey]);

  if (!hVal || !wVal) return null;

  // 10 より大きければ「cm」とみなす
  const hMeter = hVal > 10 ? hVal / 100 : hVal;

  if (hMeter <= 0) return null;

  const bmi = wVal / (hMeter * hMeter);

  // ありえそうなレンジだけ採用
  if (bmi < 5 || bmi > 80) return null;

  return bmi;
};

interface DoctorQuestionnaireProps {
  // 埋め込みモード：IDを props で受け取り、戻るボタン・タイトルを非表示
  embeddedId?: number;
  embedded?: boolean;
}

const DoctorQuestionnaire: React.FC<DoctorQuestionnaireProps> = ({ embeddedId, embedded = false }) => {
  const params = useParams<{ id: string }>();
  const id = embeddedId != null ? String(embeddedId) : params.id;
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 言語は i18n.language の変更に追従
  const lang = useMemo<LangKey>(
    () => (i18n.language.startsWith('zh') ? 'zh' : 'ja'),
    [i18n.language]
  );

  const [qaList, setQaList] = useState<{ question: string; answer: string }[]>([]);
  const [service, setService] = useState('');
  const [usedFile, setUsedFile] = useState<string | undefined>(undefined);
  const [usedGroup, setUsedGroup] = useState<UsedGroup>('unknown');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bmi, setBmi] = useState<number | null>(null); // ★ BMI 追加

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        // 1) 回答 + service を取得
        const res = await api.get(`/doctor/questionnaire/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        const answersObj: Record<string, any> =
          typeof res.data?.answers === 'string'
            ? JSON.parse(res.data.answers)
            : res.data?.answers || {};

        const serviceName: string = res.data?.service || '';
        if (!alive) return;

        setService(serviceName);

        // ★ BMI 計算（男性/女性メディカルダイエットのみ）
        const bmiVal = calcBmiFromAnswers(serviceName, answersObj);
        setBmi(bmiVal);

        // 2) 質問定義を読み込み
        const { raw, used } = await loadQuestionsWithFallback(serviceName);
        if (used) {
          setUsedFile(used);
          // eslint-disable-next-line no-console
          console.info(`[DoctorQuestionnaire] Loaded: /data/${used}`);
        } else {
          // eslint-disable-next-line no-console
          console.warn('[DoctorQuestionnaire] No question file matched.');
        }

        // 3) 題組を自動選択（初診/再診）
        const picked = pickQuestions(raw, answersObj);
        setUsedGroup(picked.group);

        // 4) 可視条件を適用（依存未回答は不可視）
        const visibleList = picked.list.filter((q) => isVisible(q, answersObj));

        // 5) 表示用に整形
        const normalizeText = (v: any): string => {
          if (Array.isArray(v)) {
            return v.length > 0 ? v.join('、') : '';
          }
          if (v === null || v === undefined) return '';
          const s = String(v);
          return s.trim();
        };

        const parsed =
          visibleList.length > 0
            ? visibleList.map((q) => {
              const a = normalizeText(answersObj[q.id]);
              return {
                question: q.text?.[lang] ?? q.id,
                answer: a || (t('doctor.no_answer') as string),
              };
            })
            // 定義が取れないときだけ回答キーでフォールバック
            : Object.keys(answersObj).map((qid, idx) => ({
              question: `${t('doctor.question')} ${idx + 1}（${qid}）`,
              answer: normalizeText(answersObj[qid]) || (t('doctor.no_answer') as string),
            }));

        if (!alive) return;
        setQaList(parsed);
        setError('');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch questionnaire:', err);
        if (!alive) return;
        setError(t('doctor.load_error'));
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchData();
    return () => {
      alive = false;
    };
  }, [id, lang, t]);

  const groupBadge =
    usedGroup === 'first'
      ? (t('visit_type.first', '初診') as string)
      : usedGroup === 'followup'
        ? (t('visit_type.followup', '再診') as string)
        : '';

  // 予約一覧に戻る（直前の検索クエリを温存）
  const goBack = () => {
    const saved = getSavedSearchForPath('/doctor/reservations');
    navigate({ pathname: '/doctor/reservations', search: saved || '' });
  };

  // BMI は肥満メニューかつ値があるときだけ表示
  const showBmiBox = service.startsWith('obesity') && bmi !== null;

  const content = (
    <div className={embedded ? 'p-3' : 'max-w-5xl mx-auto bg-white/90 rounded-xl shadow-md p-6'}>
      {/* 戻るボタン（埋め込み時は非表示） */}
      {!embedded && (
      <div className="flex justify-between items-center mb-3">
        <button onClick={goBack} className="text-coral hover:underline">
          ← {t('doctor.back')}
        </button>
        <div className="w-20" />
      </div>
      )}

      {/* タイトル + BMI ボックス（埋め込み時はタイトル非表示、BMIは表示） */}
      {(!embedded || showBmiBox) && (
      <div className={embedded ? 'mb-3' : 'mb-5 text-center'}>
        {!embedded && (
        <h1 className="text-lg font-bold flex items-center justify-center gap-2">
          {t('doctor.questionnaire_title')}：{t(`service.${service}`) || service}
          {groupBadge && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
              {groupBadge}
            </span>
          )}
        </h1>
        )}

        {showBmiBox && (
          <div className="mt-2 inline-flex flex-col items-start px-3 py-2 rounded-lg bg-coral/5 border border-blue-100 shadow-sm text-left text-xs">
            <div className="font-semibold text-gray-700 mb-0.5">
              {t('doctor.bmi_label', 'BMI')}
            </div>
            <div className="text-base font-bold text-gray-900 leading-tight">
              {bmi!.toFixed(1)}
            </div>
            <div
              className={
                bmi! < 18.5
                  ? "mt-1 text-[11px] leading-snug text-red-600 font-semibold"
                  : "mt-1 text-[11px] leading-snug text-gray-600"
              }
            >
              {bmi! < 18.5
                ? t(
                  'doctor.bmi_under_18_5',
                  '18.5未満のため、減量注射の適応外です。'
                )
                : t(
                  'doctor.bmi_over_18_5',
                  '18.5以上（減量注射の適応条件を満たす可能性があります）。'
                )}
            </div>

          </div>
        )}
      </div>
      )}

      {/* デバッグ用ファイル名（埋め込み時は非表示） */}
      {!embedded && usedFile && <p className="text-xs text-gray-400 mb-2">/data/{usedFile}</p>}

      {/* 本文 */}
      {loading ? (
        <p className="text-gray-500 text-center text-xs py-4">{t('common.loading')}</p>
      ) : error ? (
        <p className="text-red-500 text-center text-xs py-4">{error}</p>
      ) : (
        <ul className={embedded ? 'space-y-2' : 'space-y-4'}>
          {qaList.map((qa, index) => (
            <li key={index} className={embedded ? 'border-b border-gray-100 pb-2' : 'border-b pb-3'}>
              <p className={embedded ? 'text-xs font-medium text-gray-500' : 'font-semibold text-gray-800'}>
                Q{index + 1}. {qa.question}
              </p>
              <p className={embedded ? 'text-xs text-gray-800 mt-0.5 bg-gray-50 rounded px-2 py-1' : 'ml-4 text-gray-600 mt-1 whitespace-pre-line'}>
                {embedded ? qa.answer : `A. ${qa.answer}`}
              </p>
            </li>
          ))}
          {qaList.length === 0 && (
            <p className="text-gray-500 text-center text-xs py-4">{t('doctor.no_answers')}</p>
          )}
        </ul>
      )}
    </div>
  );

  return embedded ? content : content;
};

export default DoctorQuestionnaire;
